import { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { track } from '@lf/analytics-events';
import yted from '@lf/api-client/lfApi';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

// Shared 13+ standalone-student checkout. POSTs /subscription/checkout_session_child
// then redirects to Stripe Checkout. Used by both the spent-trial paywall
// (StudentSubscribePrompt) and the in-trial "Subscribe now" banner (TrialBanner) —
// subscribing during the trial forfeits the remaining days but is allowed.
//
// `surface` distinguishes the two entry points in analytics.
export function useStudentCheckout(surface) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const startCheckout = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);
        setError('');

        track('checkout_start', {
            plan: 'student',
            price_type: 'monthly_standard',
            surface,
            context: 'subscribe',
        });

        try {
            const stripe = await stripePromise;
            const response = await yted.post('/subscription/checkout_session_child', {});
            const session = response.data;

            const delay = 5000;
            setTimeout(async () => {
                track('checkout_redirect', {
                    plan: 'student',
                    price_type: 'monthly_standard',
                    surface,
                });
                const result = await stripe.redirectToCheckout({
                    sessionId: session.session_id,
                });
                if (result.error) {
                    console.error(result.error.message);
                }
            }, delay);

            await sleep(delay);
        } catch (err) {
            const errorCode = err?.response?.status || 'unknown';
            track('checkout_error', {
                plan: 'student',
                price_type: 'monthly_standard',
                surface,
                error_code: String(errorCode),
            });
            setError('Something went wrong. Please try again.');
            setIsLoading(false);
        }
    }, [isLoading, surface]);

    return { startCheckout, isLoading, error };
}
